import { useState, useEffect, useMemo, useRef } from "react";
import { api, API } from "@/lib/api";
import { Code, Copy, Broadcast, UploadSimple, Key, Plus, Eye, Planet } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const PULSE_SITES = ["", "socialpulse", "naturepulse", "travelerpulse", "foodpulse", "alumnipulse", "indianfamilytree"];
const SOURCES = [
  { value: "latest", label: "Latest photos" },
  { value: "category", label: "Category" },
  { value: "place", label: "Place" },
  { value: "query", label: "Search phrase" },
  { value: "event", label: "Event photo set" },
  { value: "entity", label: "Entity (species/landmark/tag)" },
];

const field = "mt-1 h-9 w-full rounded-md border border-border bg-white px-3 text-sm text-zinc-700 outline-none focus:border-[#F27A54]";
const lbl = "overline text-[10px] text-zinc-500";

function attrLine(k, v) { return v ? `\n     data-${k}="${v}"` : ""; }

export default function EmbedStudio() {
  const [tab, setTab] = useState("gallery");
  // gallery config
  const [g, setG] = useState({ source: "latest", id: "", q: "", place: "", category: "", type: "species", slug: "", site: "", limit: "8", columns: "3", theme: "light" });
  // submit config
  const [s, setS] = useState({ site: "", label: "Submit a photo to PhotoPulse", theme: "light" });
  const [events, setEvents] = useState([]);
  const [keys, setKeys] = useState([]);
  const [newName, setNewName] = useState("");
  const [newSite, setNewSite] = useState("");
  const [scriptReady, setScriptReady] = useState(!!window.PhotoPulseEmbed);
  const previewRef = useRef(null);

  const loadKeys = () => api.get("/apikeys").then((r) => setKeys(r.data)).catch(() => {});
  useEffect(() => {
    loadKeys();
    api.get("/public/event-sets?limit=50").then((r) => setEvents(r.data.events || [])).catch(() => {});
  }, []);

  // load the widget script once for the live preview
  useEffect(() => {
    if (window.PhotoPulseEmbed) { setScriptReady(true); return; }
    let el = document.getElementById("pp-widget-script");
    if (!el) {
      el = document.createElement("script");
      el.id = "pp-widget-script";
      el.src = `${API}/embed/widget.js`;
      el.async = true;
      el.onload = () => setScriptReady(true);
      document.body.appendChild(el);
    } else {
      el.addEventListener("load", () => setScriptReady(true));
    }
  }, []);

  const previewDiv = useMemo(() => {
    if (tab === "submit") {
      return `<div data-photopulse-submit${attrLine("site", s.site)}${attrLine("label", s.label)}${s.theme === "dark" ? attrLine("theme", "dark") : ""}></div>`;
    }
    const parts = { source: g.source };
    if (g.source === "category") parts.category = g.category;
    if (g.source === "place") parts.place = g.place;
    if (g.source === "query") parts.q = g.q;
    if (g.source === "event") parts.id = g.id;
    if (g.source === "entity") { parts.type = g.type; parts.slug = g.slug; }
    if (g.site) parts.site = g.site;
    parts.limit = g.limit; parts.columns = g.columns;
    if (g.theme === "dark") parts.theme = "dark";
    let out = "<div data-photopulse";
    Object.entries(parts).forEach(([k, v]) => { out += attrLine(k, v); });
    return out + "></div>";
  }, [tab, g, s]);

  const snippet = `${previewDiv}\n<script src="${API}/embed/widget.js" async></script>`;

  // (re)hydrate the live preview whenever config changes
  useEffect(() => {
    if (!scriptReady || !previewRef.current) return;
    const dark = (tab === "submit" ? s.theme : g.theme) === "dark";
    previewRef.current.style.background = dark ? "#0c1420" : "#ffffff";
    previewRef.current.innerHTML = previewDiv;
    if (window.PhotoPulseEmbed) window.PhotoPulseEmbed.render();
  }, [scriptReady, previewDiv, tab, g.theme, s.theme]);

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied to clipboard"); };
  const createKey = async () => {
    if (!newName) { toast.error("Give the key a name"); return; }
    await api.post("/apikeys", { name: newName, site: newSite });
    setNewName(""); setNewSite(""); loadKeys();
    toast.success("Publishable key created");
  };

  const set = (obj, k) => (e) => obj((c) => ({ ...c, [k]: e.target.value }));

  return (
    <div className="mx-auto max-w-[1400px]" data-testid="embed-studio">
      <p className="overline text-[#F27A54]">Syndication</p>
      <h1 className="mt-1 font-display text-3xl font-black tracking-tight">Embed Widgets</h1>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
        Drop a live PhotoPulse gallery or a “Submit a photo” button onto any site — sister Pulse sites
        or a completely external website. One <code className="rounded bg-zinc-100 px-1 py-0.5">&lt;script&gt;</code> tag,
        canonical back-links, and a review-gated pipeline for incoming submissions.
      </p>

      {/* Tabs */}
      <div className="mt-6 inline-flex rounded-md border border-border bg-white p-1" data-testid="embed-tabs">
        <button onClick={() => setTab("gallery")} data-testid="embed-tab-gallery"
          className={`flex items-center gap-1.5 rounded px-4 py-1.5 text-sm font-bold transition-colors ${tab === "gallery" ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>
          <Broadcast size={15} weight="bold" /> Gallery embed
        </button>
        <button onClick={() => setTab("submit")} data-testid="embed-tab-submit"
          className={`flex items-center gap-1.5 rounded px-4 py-1.5 text-sm font-bold transition-colors ${tab === "submit" ? "bg-[#F27A54] text-white" : "text-zinc-600 hover:bg-zinc-50"}`}>
          <UploadSimple size={15} weight="bold" /> Submission widget
        </button>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {/* ---------- Config ---------- */}
        <Card className="rounded-md border border-border p-5 shadow-none">
          <h3 className="font-display font-bold">Configure</h3>

          {tab === "gallery" ? (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={lbl}>What to show</label>
                <select className={field} value={g.source} onChange={set(setG, "source")} data-testid="embed-source">
                  {SOURCES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              {g.source === "category" && (
                <div className="col-span-2"><label className={lbl}>Category</label>
                  <select className={field} value={g.category} onChange={set(setG, "category")} data-testid="embed-category">
                    <option value="">Choose…</option>
                    {["Nature", "Travel", "Food", "Geographic", "Institutions", "Events", "Objects"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              {g.source === "place" && (
                <div className="col-span-2"><label className={lbl}>Place (city / state / country)</label>
                  <Input className="mt-1 h-9 rounded-md" value={g.place} onChange={set(setG, "place")} placeholder="e.g. Iceland" data-testid="embed-place" /></div>
              )}
              {g.source === "query" && (
                <div className="col-span-2"><label className={lbl}>Search phrase</label>
                  <Input className="mt-1 h-9 rounded-md" value={g.q} onChange={set(setG, "q")} placeholder="e.g. snowy peaks" data-testid="embed-q" /></div>
              )}
              {g.source === "event" && (
                <div className="col-span-2"><label className={lbl}>Event photo set</label>
                  <select className={field} value={g.id} onChange={set(setG, "id")} data-testid="embed-event">
                    <option value="">Choose a published event…</option>
                    {events.map((e) => <option key={e.evt_id} value={e.evt_id}>{`${e.title} (${e.evt_id})`}</option>)}
                  </select>
                  {events.length === 0 && <p className="mt-1 text-[11px] text-amber-600">No published event sets yet — publish one under Event Photo Sets.</p>}
                </div>
              )}
              {g.source === "entity" && (
                <>
                  <div><label className={lbl}>Entity type</label>
                    <select className={field} value={g.type} onChange={set(setG, "type")} data-testid="embed-entity-type">
                      {["species", "landmark", "tag", "place", "food", "domain"].map((t) => <option key={t} value={t}>{t}</option>)}
                    </select></div>
                  <div><label className={lbl}>Slug</label>
                    <Input className="mt-1 h-9 rounded-md" value={g.slug} onChange={set(setG, "slug")} placeholder="e.g. bald-eagle" data-testid="embed-entity-slug" /></div>
                </>
              )}
              <div><label className={lbl}>Photos</label>
                <select className={field} value={g.limit} onChange={set(setG, "limit")} data-testid="embed-limit">
                  {["4", "6", "8", "12", "16", "24"].map((n) => <option key={n} value={n}>{n}</option>)}
                </select></div>
              <div><label className={lbl}>Columns</label>
                <select className={field} value={g.columns} onChange={set(setG, "columns")} data-testid="embed-columns">
                  {["1", "2", "3", "4", "5"].map((n) => <option key={n} value={n}>{n}</option>)}
                </select></div>
              <div><label className={lbl}>Theme</label>
                <select className={field} value={g.theme} onChange={set(setG, "theme")} data-testid="embed-theme">
                  <option value="light">Light</option><option value="dark">Dark</option>
                </select></div>
              <div><label className={lbl}>Restrict to site (optional)</label>
                <select className={field} value={g.site} onChange={set(setG, "site")} data-testid="embed-site">
                  {PULSE_SITES.map((v) => <option key={v || "any"} value={v}>{v || "Any (all public)"}</option>)}
                </select></div>
            </div>
          ) : (
            <div className="mt-3 grid grid-cols-1 gap-3">
              <div><label className={lbl}>Button label</label>
                <Input className="mt-1 h-9 rounded-md" value={s.label} onChange={set(setS, "label")} data-testid="embed-submit-label" /></div>
              <div><label className={lbl}>Attribute submissions to site (optional)</label>
                <select className={field} value={s.site} onChange={set(setS, "site")} data-testid="embed-submit-site">
                  {PULSE_SITES.map((v) => <option key={v || "any"} value={v}>{v || "External / unspecified"}</option>)}
                </select></div>
              <div><label className={lbl}>Theme</label>
                <select className={field} value={s.theme} onChange={set(setS, "theme")} data-testid="embed-submit-theme">
                  <option value="light">Light</option><option value="dark">Dark</option>
                </select></div>
              <p className="rounded-md bg-emerald-50 p-3 text-[12px] leading-relaxed text-emerald-800">
                Submissions go straight into your <b>Review Queue</b> (private, AI-tagged) — nothing is published automatically.
                Protected by a honeypot + per-IP rate limit.
              </p>
            </div>
          )}
        </Card>

        {/* ---------- Preview + snippet ---------- */}
        <div className="space-y-4">
          <Card className="rounded-md border border-border p-5 shadow-none">
            <div className="flex items-center gap-2"><Eye size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Live preview</h3></div>
            <div ref={previewRef} data-testid="embed-preview"
              className="mt-3 min-h-[220px] rounded-md border border-dashed border-border p-3" />
          </Card>

          <Card className="rounded-md border border-border p-5 shadow-none">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Code size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Copy this snippet</h3></div>
              <Button size="sm" className="h-8 gap-1 rounded-md bg-[#2D6A4F] hover:bg-[#245840]" onClick={() => copy(snippet)} data-testid="embed-copy">
                <Copy size={13} /> Copy
              </Button>
            </div>
            <pre className="mt-3 overflow-x-auto rounded-md bg-zinc-900 p-4 font-mono-p text-[11px] leading-relaxed text-zinc-100" data-testid="embed-snippet">{snippet}</pre>
            <p className="mt-2 text-xs text-muted-foreground">Paste it anywhere in the page HTML. It self-styles (Shadow DOM) and only ever shows publicly-reusable photos with links back to PhotoPulse.</p>
          </Card>
        </div>
      </div>

      {/* ---------- Publishable keys ---------- */}
      <Card className="mt-6 rounded-md border border-border p-5 shadow-none" data-testid="embed-keys">
        <div className="flex items-center gap-2"><Key size={18} weight="bold" className="text-[#F27A54]" /><h3 className="font-display font-bold">Publishable keys</h3></div>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          A <b>publishable key</b> (<code className="rounded bg-zinc-100 px-1 py-0.5">ppk_…</code>) is safe to expose in browser embeds — add
          <code className="mx-1 rounded bg-zinc-100 px-1 py-0.5">{'data-key="ppk_…"'}</code> to a submission widget to attribute incoming photos to a site.
          Keep the secret <code className="rounded bg-zinc-100 px-1 py-0.5">pk_live_…</code> key server-side only.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div><label className={lbl}>Key name</label>
            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. WeatherPulse widget" className="mt-1 h-9 w-56 rounded-md" data-testid="embed-key-name" /></div>
          <div><label className={lbl}>Site (optional)</label>
            <Input value={newSite} onChange={(e) => setNewSite(e.target.value)} placeholder="weatherpulse" className="mt-1 h-9 w-52 rounded-md" data-testid="embed-key-site" /></div>
          <Button onClick={createKey} className="h-9 gap-1 rounded-md bg-[#F27A54] hover:bg-[#d9633f]" data-testid="embed-key-create"><Plus size={15} weight="bold" /> Generate</Button>
        </div>
        <div className="mt-4 space-y-2">
          {keys.filter((k) => k.pub_key).map((k) => (
            <div key={k.id} className="flex items-center justify-between gap-3 rounded-md border border-border px-3 py-2" data-testid={`embed-key-row-${k.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{k.name} {k.site && <span className="text-xs font-normal text-muted-foreground">· {k.site}</span>}</p>
                <p className="truncate font-mono-p text-xs text-emerald-700">{k.pub_key}</p>
              </div>
              <Button size="sm" variant="outline" className="h-8 gap-1 rounded-md" onClick={() => copy(k.pub_key)} data-testid={`embed-key-copy-${k.id}`}><Copy size={13} /> Copy</Button>
            </div>
          ))}
          {keys.filter((k) => k.pub_key).length === 0 && <p className="text-sm text-muted-foreground">No publishable keys yet — generate one above (optional; embeds work without a key too).</p>}
        </div>
      </Card>

      <p className="mt-6 flex items-center gap-1.5 text-xs text-muted-foreground"><Planet size={13} weight="fill" className="text-[#F27A54]" /> Widgets render only approved, publicly-reusable photos — private and exact-GPS data is never exposed.</p>
    </div>
  );
}
